import { AppConfiguration, SLOT, TIER } from 'studiokit-scaffolding-js'
import packageInfo from '../package.json'

const windowEnv: ImportMetaEnv = (window as any)._env_ || {}
const processEnv = import.meta.env

/**
 * Global Configuration values for the app
 *
 * Combines values from the following sources:
 * * Defaults values in this file
 *   * Values that will not change based on tier
 * * Environment Variables prefixed with `VITE_`
 *   * Useful for local development using `.env` files and `env-cmd`
 * * `public/env.js`
 *   * A static file that adds overrides to `windowEnv` and can be modified after build at runtime
 */
const config: AppConfiguration = {
	NODE_ENV: processEnv.PROD ? 'production' : 'development',

	// App Version + Name
	APP_NAME: 'ink2html',
	VERSION: packageInfo.version,
	VERSION_NAME: '"Bottletail Squid"',

	// Tier and URLs
	TIER: (windowEnv.VITE_TIER as TIER | undefined) || (processEnv.VITE_TIER as TIER | undefined) || TIER.LOCAL,
	SLOT: (windowEnv.VITE_SLOT as SLOT | undefined) || (processEnv.VITE_SLOT as SLOT | undefined) || SLOT.PRODUCTION,
	ROOT_DOMAIN: 'ink2html.org',
	IS_SHARDED: false,
	API_URL: windowEnv.VITE_API_URL || processEnv.VITE_API_URL || '',
	LOCALHOST_SHARD_KEY: windowEnv.VITE_LOCALHOST_SHARD_KEY || processEnv.VITE_LOCALHOST_SHARD_KEY || undefined,
	STORAGE_URL: windowEnv.VITE_STORAGE_URL || processEnv.VITE_STORAGE_URL || undefined,

	// Google Analytics
	GOOGLE_ANALYTICS_TRACKING_ID:
		windowEnv.VITE_GOOGLE_ANALYTICS_TRACKING_ID || processEnv.VITE_GOOGLE_ANALYTICS_TRACKING_ID || undefined,

	// Sentry
	SENTRY_DSN: windowEnv.VITE_SENTRY_DSN || processEnv.VITE_SENTRY_DSN || undefined,
	SENTRY_TRACES_SAMPLE_RATE:
		windowEnv.VITE_SENTRY_TRACES_SAMPLE_RATE || processEnv.VITE_SENTRY_TRACES_SAMPLE_RATE
			? Number(windowEnv.VITE_SENTRY_TRACES_SAMPLE_RATE || processEnv.VITE_SENTRY_TRACES_SAMPLE_RATE)
			: undefined,
	SENTRY_REPLAYS_SESSION_SAMPLE_RATE:
		windowEnv.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE || processEnv.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE
			? Number(
					windowEnv.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE ||
						processEnv.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE
				)
			: undefined,
	SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE:
		windowEnv.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE || processEnv.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE
			? Number(
					windowEnv.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE ||
						processEnv.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE
				)
			: undefined,

	// Application Insights
	APP_INSIGHTS_CONNECTION_STRING:
		windowEnv.VITE_APPINSIGHTS_CONNECTION_STRING || processEnv.VITE_APPINSIGHTS_CONNECTION_STRING || undefined,

	// OAuth
	CLIENT_ID: windowEnv.VITE_CLIENT_ID || processEnv.VITE_CLIENT_ID || '', // MUST set in env
	OAUTH_PKCE_SCOPE:
		windowEnv.VITE_OAUTH_PKCE_SCOPE ||
		processEnv.VITE_OAUTH_PKCE_SCOPE ||
		'enrollment:own_enrollment:read users:own_profile:read', // default scope, but allow override at runtime if needed
	OAUTH_PKCE_ENDPOINT: 'https://auth.brightspace.com/oauth2/auth',

	SESSION_IDLE_THRESHOLD:
		windowEnv.VITE_SESSION_IDLE_THRESHOLD || processEnv.VITE_SESSION_IDLE_THRESHOLD
			? Number(windowEnv.VITE_SESSION_IDLE_THRESHOLD || processEnv.VITE_SESSION_IDLE_THRESHOLD)
			: 1000 * 60 * 15, // default to 15 minutes

	SESSION_REFRESH_THRESHOLD:
		windowEnv.VITE_SESSION_REFRESH_THRESHOLD || processEnv.VITE_SESSION_REFRESH_THRESHOLD
			? Number(windowEnv.VITE_SESSION_REFRESH_THRESHOLD || processEnv.VITE_SESSION_REFRESH_THRESHOLD)
			: 1000 * 60 * 5, // default to 5 minutes

	// Downtime
	IS_DOWNTIME: Boolean(windowEnv.VITE_IS_DOWNTIME) || Boolean(processEnv.VITE_IS_DOWNTIME) || false,

	// FunctionApp
	FUNCTION_APP_CODE: windowEnv.VITE_FUNCTION_APP_CODE || processEnv.VITE_FUNCTION_APP_CODE || ''
}

export default config
